// Player state machine (docs/04 lifecycle: PAIRING ⇄ RUNNING).
//
// Owns the transitions and the manifest poll cadence; delegates rendering to the
// pairing screen and the playback engine, delivery to the API client + media
// cache, and recovery to the watchdog. The singletons (config, log, cache, api)
// live across state changes; the playback engine + watchdog are created fresh on
// each entry into RUNNING and torn down if the token is revoked.

import { PlayerApi, UnauthorizedError, PLAYER_VERSION } from './api.ts';
import type { Heartbeat, PlayerApiLike } from './api.ts';
import { loadConfig } from './config.ts';
import type { Config } from './config.ts';
import { LogBuffer } from './logbuffer.ts';
import { MediaCache } from './cache.ts';
import { MockApi } from './mock.ts';
import { runPairing } from './pairing.ts';
import { selectActiveWindow } from '@seencast/player-core';
import { ZoneStage } from './zonestage.ts';
import { LiveConnection } from './live.ts';
import type { NowPlaying } from './live.ts';
import { liveWsUrl } from './config.ts';
import { isFullyCached, mediaUrlsOf } from './cacheplan.ts';
import {
  loadEvents,
  loadIdentity,
  loadManifest,
  saveEvents,
  saveIdentity,
  saveManifest,
  wipeIdentity,
} from './storage.ts';
import type { DeviceIdentity } from './storage.ts';
import { Watchdog } from './watchdog.ts';
import { EventBuffer, mediaIdOf } from './events.ts';
import type { PlaybackEvent } from './events.ts';
import { NativeBridge, SnapshotController } from './snapshot.ts';
import { base64ToJpegBlob, captureCurrentFrame } from './capture-dom.ts';
import { makeId } from './id.ts';
import { hash32 } from './reload.ts';
import { applyJitter, resolvePollIntervalS } from './pollinterval.ts';
import { estimateStorage, formatStorageUsage, requestPersistentStorage } from './quota.ts';
import type { Manifest, ManifestItem, ManifestItemType } from './types.ts';

async function applySwUpdate(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration();
  reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

export class PlayerApp {
  private cfg: Config;
  private log: LogBuffer;
  private cache: MediaCache;
  private api: PlayerApiLike;
  private bootedAt = Date.now();
  private token: string | null = null;

  // RUNNING-scoped state.
  private zoneStage: ZoneStage | null = null;
  private watchdog: Watchdog | null = null;
  private events: EventBuffer | null = null;
  private snapshot: SnapshotController | null = null;
  private live: LiveConnection | null = null;
  private stateTimer: ReturnType<typeof setInterval> | null = null;
  private activeManifest: Manifest | null = null;
  private etag: string | null = null;
  private cacheOk = true;
  /** Resolves the current poll-loop sleep early so a live `refresh_manifest` polls now. */
  private pollWaker: (() => void) | null = null;
  /** Device-stable [0,1) seed for ±10% poll jitter (fleet spread). Set on RUNNING. */
  private pollJitterSeed = 0;

  constructor(private appEl: HTMLElement) {
    this.cfg = loadConfig();
    this.log = new LogBuffer(this.cfg, () => this.token);
    this.cache = new MediaCache(
      this.cfg,
      () => this.token,
      (code, message) => this.log.add('warn', code, message),
    );
    this.api = this.cfg.mock ? new MockApi() : new PlayerApi(this.cfg);
    if (this.cfg.mock) this.exposeTestHooks();
  }

  async boot(): Promise<void> {
    this.log.start();
    // State machine loop: PAIRING → RUNNING → (token revoked) → PAIRING → …
    for (;;) {
      let identity = await loadIdentity();
      if (!identity) {
        const paired = await runPairing(this.appEl, this.api, this.cfg, this.log);
        identity = { token: paired.device_token, screenId: paired.screen_id };
        await saveIdentity(identity);
      }
      this.token = identity.token;
      await this.runRunning(identity); // returns only on token revocation
      // Revoked: wipe device state + media, then re-pair.
      await wipeIdentity();
      await this.cache.clear();
      this.token = null;
      this.activeManifest = null;
      this.etag = null;
    }
  }

  private async runRunning(identity: DeviceIdentity): Promise<void> {
    // Ask the WebView to keep our origin storage persistent (prompt-less grant in
    // kiosk contexts); best-effort, logged, never blocks the boot.
    void requestPersistentStorage((code, message) => this.log.add('info', code, message));
    // Device-stable jitter seed so this device keeps a consistent ±10% poll offset.
    this.pollJitterSeed = hash32(identity.token) / 0xffffffff;

    // Fresh stage for the playback engine.
    this.appEl.replaceChildren();
    const stage = document.createElement('div');
    stage.id = 'stage';
    this.appEl.appendChild(stage);

    // Playback analytics: the engine reports each item's actual on-screen time;
    // we buffer them (persisted, deduped, ack-only-drop) and flush periodically.
    this.events = new EventBuffer(
      { load: loadEvents, save: saveEvents },
      (batch) => this.api.postEvents(identity.token, batch),
      (e) => this.log.add('warn', 'events_flush_failed', String(e)),
      (dropped) => this.log.add('warn', 'buffer_overflow', `dropped ${dropped} oldest events`),
    );
    this.zoneStage = new ZoneStage(stage, this.cache, this.log, {
      onItemPlayed: (item, playedMs) => this.recordPlayed(item, playedMs),
    });
    this.snapshot = this.buildSnapshotController(identity.token);
    this.watchdog = new Watchdog({
      msSinceFrame: () => this.zoneStage?.msSinceFrame() ?? 0,
      token: identity.token,
      log: (code, message) => this.log.add('warn', code, message),
      applyUpdate: applySwUpdate,
      reload: this.cfg.mock ? () => this.markReloaded() : undefined,
    });
    this.live = this.buildLive(identity.token);

    // Cold boot: play the persisted manifest immediately (works fully offline).
    const persisted = await loadManifest();
    if (persisted) {
      this.activeManifest = persisted;
      this.etag = persisted.version;
      this.cacheOk = isFullyCached(persisted, await this.cachedNow());
      this.zoneStage.setManifest(persisted);
    }
    this.zoneStage.start();
    this.watchdog.start();
    this.events.start();
    this.live?.start();
    // Push now-playing to the live hub on change (throttled inside LiveConnection).
    if (this.live) this.stateTimer = setInterval(() => this.live?.notifyStateChanged(), 1000);

    // Manifest poll loop — the heartbeat rides every request.
    try {
      for (;;) {
        await this.pollOnce(identity.token);
        // Traffic diet (T3): stretch the interval while the live WS is connected
        // (the hub pushes refresh_manifest), then jitter ±10% for fleet spread.
        const intervalS = resolvePollIntervalS(
          this.activeManifest?.poll_interval_s ?? null,
          this.live?.isConnected() ?? false,
        );
        await this.waitBeforeNextPoll(applyJitter(intervalS * 1000, this.pollJitterSeed));
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        this.log.add('warn', 'token_revoked');
        this.teardownRunning();
        return;
      }
      throw e;
    }
  }

  private async pollOnce(token: string): Promise<void> {
    // Storage telemetry (T2): every poll reports usage + the pressure bit (D3 reads
    // X-Storage-Pressure/-Usage). Usage is a fresh estimate(); the byte ledger that
    // drives the pre-flight swap decision is kept LOCAL and never feeds the header.
    const storage = await estimateStorage();
    const hb: Heartbeat = {
      uptimeS: Math.round((Date.now() - this.bootedAt) / 1000),
      currentItem: this.zoneStage?.getCurrentItemId() ?? null,
      cacheOk: this.cacheOk,
      storagePressure: this.cache.storagePressure(),
      storageUsage: formatStorageUsage(storage),
    };
    let res;
    try {
      res = await this.api.fetchManifest(token, this.etag, hb);
    } catch (e) {
      if (e instanceof UnauthorizedError) throw e;
      // Network error → keep playing from cache, retry next interval.
      this.log.add('warn', 'manifest_fetch_failed', String(e));
      return;
    }
    // A command (e.g. 'snapshot') can ride a 200 OR a 304 — handle it before the
    // 304 early-return so an idle-but-current screen still responds.
    if (res.command === 'snapshot') void this.snapshot?.onCommand();
    if (!res.manifest) return; // 304 Not Modified
    // A fresh manifest (200) is one of the event-flush triggers.
    void this.events?.flush();

    // New manifest: cache ALL its media first, then swap atomically.
    const next = res.manifest;
    const ready = await this.cache.ensureCached(next);
    if (!ready) {
      this.cacheOk = false;
      this.log.add('warn', 'manifest_media_incomplete', next.version);
      return; // keep old manifest; retry next poll (don't advance etag)
    }
    this.activeManifest = next;
    this.etag = res.etag ?? next.version;
    this.cacheOk = true;
    this.zoneStage?.setManifest(next);
    await saveManifest(next);
    await this.cache.prune(next);
    this.log.add('info', 'manifest_applied', next.version);
  }

  private async cachedNow(): Promise<string[]> {
    // Present media URLs, used to compute cacheOk for the persisted manifest.
    if (!this.activeManifest) return [];
    const needed = mediaUrlsOf(this.activeManifest);
    const present: string[] = [];
    for (const u of needed) if ((await this.cache.resolveSrc(u)) !== null) present.push(u);
    return present;
  }

  private teardownRunning(): void {
    this.zoneStage?.stop();
    this.watchdog?.stop();
    this.events?.stop();
    this.live?.stop();
    if (this.stateTimer) clearInterval(this.stateTimer);
    this.pollWaker = null;
    this.zoneStage = null;
    this.watchdog = null;
    this.events = null;
    this.snapshot = null;
    this.live = null;
    this.stateTimer = null;
  }

  /**
   * Poll-loop sleep that a live `refresh_manifest` command can cut short. Stores a
   * waker; `wakePoll()` resolves it so the loop runs `pollOnce` immediately (no
   * concurrent fetch — control simply returns to the single poll loop).
   */
  private waitBeforeNextPoll(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const t = setTimeout(() => {
        this.pollWaker = null;
        resolve();
      }, ms);
      this.pollWaker = () => {
        clearTimeout(t);
        this.pollWaker = null;
        resolve();
      };
    });
  }

  private wakePoll(): void {
    this.pollWaker?.();
  }

  /** Turn a player-core `onItemPlayed` into a buffered analytics event. */
  private recordPlayed(item: ManifestItem, playedMs: number): void {
    const mediaId = mediaIdOf(item);
    if (!mediaId || !this.events) return; // no attributable media (only old-manifest type=url)
    const now = Date.now();
    const ev: PlaybackEvent = {
      client_event_id: makeId(),
      media_id: mediaId,
      started_at: now - playedMs,
      duration_ms: playedMs,
    };
    const win = this.activeManifest ? selectActiveWindow(this.activeManifest, now) : null;
    if (win) ev.playlist_id = win.playlist_id;
    void this.events.add(ev);
  }

  private buildSnapshotController(token: string): SnapshotController {
    return new SnapshotController({
      native: new NativeBridge(),
      currentType: () => this.currentItemType(),
      // Capture the MAIN zone's stage (== the whole stage when there are no zones).
      captureDom: () =>
        captureCurrentFrame(this.zoneStage?.mainStageEl() ?? this.appEl, this.activeManifest),
      upload: (kind, body, width, height) =>
        this.api.postSnapshot(token, { kind, body, width, height }),
      makeId,
      base64ToBlob: base64ToJpegBlob,
      onError: (e) => this.log.add('warn', 'snapshot_failed', String(e)),
    });
  }

  /**
   * The live control-channel client — real-time presence + push commands (docs/04
   * §Live channel). Never in mock mode (no backend) or where WebSocket is missing;
   * in both cases the player runs exactly as today off manifest polling alone.
   */
  private buildLive(token: string): LiveConnection | null {
    if (this.cfg.mock || typeof WebSocket === 'undefined') return null;
    return new LiveConnection({
      url: liveWsUrl(this.cfg),
      token,
      playerVersion: PLAYER_VERSION,
      nowPlaying: () => this.currentNowPlaying(),
      onSnapshot: () => void this.snapshot?.onCommand(),
      onRefreshManifest: () => this.wakePoll(),
      // WS dropped → restore the normal cadence (isConnected() now false) and poll
      // once immediately by cutting the current (possibly stretched) sleep short.
      onDisconnect: () => this.wakePoll(),
      log: (code, message) => this.log.add('warn', code, message),
    });
  }

  /** The item currently on screen in the main zone, or null (idle / not started). */
  private currentItem(): ManifestItem | null {
    const id = this.zoneStage?.getCurrentItemId();
    if (!id || !this.activeManifest) return null;
    const win = selectActiveWindow(this.activeManifest, Date.now());
    return (
      win?.items.find((i) => i.id === id) ??
      this.activeManifest.fallback.items.find((i) => i.id === id) ??
      null
    );
  }

  /** Manifest type of the item currently on screen (for the DOM-capture decision). */
  private currentItemType(): ManifestItemType | null {
    return this.currentItem()?.type ?? null;
  }

  /** What the live hub shows as "now playing" for this screen (main zone). */
  private currentNowPlaying(): NowPlaying | null {
    const item = this.currentItem();
    if (!item) return null;
    const media_id = mediaIdOf(item);
    return media_id ? { media_id } : null;
  }

  // --- Test hooks (mock mode only) ---------------------------------------------
  private exposeTestHooks(): void {
    const hooks = {
      version: PLAYER_VERSION,
      activeVersion: () => this.activeManifest?.version ?? null,
      currentItem: () => this.zoneStage?.getCurrentItemId() ?? null,
      tick: () => this.zoneStage?.getTick() ?? 0,
      cacheOk: () => this.cacheOk,
      serveV2: () => (this.api as MockApi).serveV2(),
      serveZones: () => (this.api as MockApi).serveZones(),
      injectFreeze: () => {
        this.zoneStage?.forceFrozenForTest();
        this.watchdog?.checkFreezeNow();
      },
      reloaded: false,
    };
    (window as unknown as { __player: typeof hooks }).__player = hooks;
  }

  private markReloaded(): void {
    const w = window as unknown as { __player?: { reloaded: boolean } };
    if (w.__player) w.__player.reloaded = true;
  }
}
